import React, { Fragment, useState, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Key, Trash2, CheckCircle, XCircle, QrCode, Flashlight } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import QrScanner from 'qr-scanner';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface TokenVerificationProps {
  user: any;
  metefyTokens: Array<{
    token: string;
    password?: string;
    dateAdded: string;
    name?: string | null;
    call?: string | null;
    whatsapp?: string | null;
    x_username?: string | null;
    id?: string;
  }>;
  onTokensUpdate: (tokens: Array<{
    token: string;
    password?: string;
    dateAdded: string;
    name?: string | null;
    call?: string | null;
    whatsapp?: string | null;
    x_username?: string | null;
    id?: string;
  }>) => void;
  onProfileUpdate?: (updates: any) => void;
}

export const TokenVerification: React.FC<TokenVerificationProps> = ({
  user,
  metefyTokens,
  onTokensUpdate,
  onProfileUpdate,
}) => {
  const { toast } = useToast();
  const [tokenVerified, setTokenVerified] = useState<boolean | null>(null);
  const [verificationInProgress, setVerificationInProgress] = useState(false);
  const [tokenData, setTokenData] = useState({ token: "", password: "" });
  const [isDeleting, setIsDeleting] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>(Array(12).fill(null));
  
  // QR Scanner state
  const [isScanning, setIsScanning] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [qrScannerInstance, setQrScannerInstance] = useState<QrScanner | null>(null);
  const [isTorchOn, setIsTorchOn] = useState(false);

  const handleTokenDigitChange = (index: number, value: string) => {
    const cleaned = value.replace(/\D/g, "").slice(0, 1);
    const current = tokenData.token.padEnd(12, " ").split("");
    current[index] = cleaned || "";
    const nextToken = current.join("").trim();
    setTokenData(prev => ({ ...prev, token: nextToken }));
    if (cleaned && index < 11) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !tokenData.token[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleTokenChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.replace(/\D/g, "");
    setTokenData(prev => ({ ...prev, password: val }));
  };

  const startQrScan = async () => {
    try {
      setIsScanning(true);
      setShowScanner(true);

      const originalWarn = console.warn;
      const originalError = console.error;
      const suppressWarnings = () => {
        console.warn = (...args: any[]) => {
          const message = args[0]?.toString() || '';
          if (message.includes('getUserMedia') || message.includes('QR Scanner') || message.includes('camera')) {
            return;
          }
          originalWarn.apply(console, args);
        };
        console.error = (...args: any[]) => {
          const message = args[0]?.toString() || '';
          if (message.includes('getUserMedia') || message.includes('QR Scanner') || message.includes('camera')) {
            return;
          }
          originalError.apply(console, args);
        };
      };

      const restoreConsole = () => {
        console.warn = originalWarn;
        console.error = originalError;
      };

      suppressWarnings();

      setTimeout(async () => {
        try {
          const scannerContainer = document.getElementById('profile-qr-scanner-container');
          if (!scannerContainer) {
            restoreConsole();
            throw new Error('Scanner container not found');
          }

          scannerContainer.innerHTML = '';

          const videoElem = document.createElement('video');
          videoElem.style.width = '100%';
          videoElem.style.height = '100%';
          videoElem.style.objectFit = 'cover';
          scannerContainer.appendChild(videoElem);

          const qrScanner = new QrScanner(
            videoElem,
            (result) => {
              restoreConsole();
              handleQrResult(result.data);
              qrScanner.stop();
              setQrScannerInstance(null);
              setShowScanner(false);
              setIsScanning(false);
            },
            {
              onDecodeError: () => {},
              highlightScanRegion: true,
              highlightCodeOutline: true,
            }
          );

          setQrScannerInstance(qrScanner);
          await qrScanner.start();
        } catch (innerError) {
          restoreConsole();
          console.error('Error in QR scanner initialization:', innerError);
          toast({
            variant: "destructive",
            title: "Scanner Error",
            description: "Could not initialize QR scanner. Please ensure camera permissions are granted.",
          });
          setShowScanner(false);
          setIsScanning(false);
        }
      }, 100);
    } catch (error) {
      console.error('Error starting QR scanner:', error);
      toast({
        variant: "destructive",
        title: "Scanner Error",
        description: "Could not access camera for QR scanning. Please ensure camera permissions are granted.",
      });
      setShowScanner(false);
      setIsScanning(false);
    }
  };

  const handleQrResult = (qrData: string) => {
    try {
      const url = new URL(qrData);

      if (!url.hostname.includes('metefy.com')) {
        toast({
          variant: "destructive",
          title: "Invalid QR Code",
          description: "This QR code is not from Metefy.",
        });
        return;
      }

      const tokenNumber = url.searchParams.get('token');
      const shortTokenNumber = url.searchParams.get('t');
      const ownerNumber = url.searchParams.get('owner');
      const type = url.searchParams.get('type');
      const number = url.searchParams.get('number');

      let extractedToken = '';

      if (tokenNumber || shortTokenNumber || ownerNumber) {
        extractedToken = tokenNumber || shortTokenNumber || ownerNumber || '';
      } else if (type && number) {
        const normalizedType = type.toLowerCase();
        if (normalizedType === 'metefytoken' || normalizedType === 'token') {
          extractedToken = number;
        } else {
          toast({
            variant: "destructive",
            title: "Invalid QR Code",
            description: "This QR code is not for a Metefy token. Please scan a token QR code.",
          });
          return;
        }
      } else {
        toast({
          variant: "destructive",
          title: "Invalid QR Code",
          description: "QR code does not contain valid Metefy token information.",
        });
        return;
      }

      if (extractedToken.length !== 12) {
        toast({
          variant: "destructive",
          title: "Invalid Token",
          description: "Scanned token must be exactly 12 digits.",
        });
        return;
      }

      const tokenArray = extractedToken.split('');
      tokenArray.forEach((digit, index) => {
        if (inputRefs.current[index]) {
          inputRefs.current[index]!.value = digit;
        }
      });
      setTokenData(prev => ({ ...prev, token: extractedToken }));

      setTimeout(() => {
        const passwordInput = document.getElementById('token-password') as HTMLInputElement;
        if (passwordInput) {
          passwordInput.focus();
        }
      }, 300);

      toast({
        title: "QR Code Scanned",
        description: "Token has been filled. Please enter the password.",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Invalid QR Code",
        description: "Could not parse QR code data.",
      });
    }
  };

  const toggleTorch = async () => {
    if (qrScannerInstance) {
      try {
        if (isTorchOn) {
          await qrScannerInstance.turnFlashOff();
          setIsTorchOn(false);
        } else {
          await qrScannerInstance.turnFlashOn();
          setIsTorchOn(true);
        }
      } catch (error) {
        toast({
          variant: "destructive",
          title: "Flashlight Error",
          description: "Could not toggle flashlight. Your device may not support this feature.",
        });
      }
    }
  };

  const handleVerifyToken = async () => {
    if (!tokenData.token || tokenData.token.length !== 12 || !tokenData.password) {
      toast({ variant: "destructive", title: "Missing Information", description: "Please enter 12-digit token and password." });
      return;
    }
    setVerificationInProgress(true);
    setTokenVerified(null);
    try {
      const { data: tokenCheck, error: tokenError } = await supabase
        .from('metefy_tokens')
        .select('*, owner_info, photo_url')
        .eq('token', tokenData.token)
        .maybeSingle();
      if (tokenError) throw tokenError;
      if (!tokenCheck) {
        setTokenVerified(false);
        toast({ variant: "destructive", title: "Verification Failed", description: "This token does not exist." });
        return;
      }
      if (tokenCheck && tokenCheck.user_id && user && tokenCheck.user_id !== user.id) {
        setTokenVerified(false);
        toast({ variant: "destructive", title: "Verification Failed", description: "This token is already registered to another user." });
        return;
      }
      if (tokenCheck && tokenCheck.password === tokenData.password) {
        if (!tokenCheck.user_id && user) {
          const { error: updateError } = await supabase
            .from('metefy_tokens')
            .update({ user_id: user.id, is_verified: true })
            .eq('id', tokenCheck.id);
          if (updateError) throw updateError;
        }
        setTokenVerified(true);
        toast({ title: "Token Verified", description: user ? "Token linked to your account." : "Token verified." });
        setTokenData({ token: "", password: "" });
        
        if (user) {
          const { data: updated } = await supabase
            .from('metefy_tokens')
            .select('*')
            .eq('user_id', user.id)
            .eq('is_verified', true)
            .order('created_at', { ascending: false });
          if (updated) {
            const formattedTokens = updated.map(t => ({
              token: t.token,
              password: t.password,
              dateAdded: new Date(t.created_at).toISOString().split('T')[0],
              name: t.name || null,
              call: t.call || null,
              whatsapp: t.whatsapp || null,
              x_username: t.x_username || null,
              id: t.id
            }));
            onTokensUpdate(formattedTokens);
            
            if (tokenCheck && (tokenCheck.name || tokenCheck.call || tokenCheck.whatsapp || tokenCheck.x_username || tokenCheck.owner_info || tokenCheck.photo_url)) {
              const profileUpdate: any = {};
              if (tokenCheck.name && onProfileUpdate) {
                const nameParts = tokenCheck.name.trim().split(' ');
                profileUpdate.first_name = nameParts[0] || null;
                profileUpdate.last_name = nameParts.slice(1).join(' ') || null;
              }
              if (tokenCheck.call && onProfileUpdate) {
                profileUpdate.social_call = tokenCheck.call;
              }
              if (tokenCheck.whatsapp && onProfileUpdate) {
                profileUpdate.social_whatsapp = tokenCheck.whatsapp;
              }
              if (tokenCheck.x_username && onProfileUpdate) {
                profileUpdate.x_username = tokenCheck.x_username;
              }
              if (tokenCheck.owner_info && onProfileUpdate) {
                profileUpdate.bio = tokenCheck.owner_info;
              }
              if (tokenCheck.photo_url && onProfileUpdate) {
                profileUpdate.photo_url = tokenCheck.photo_url;
              }
              
              if (Object.keys(profileUpdate).length > 0 && onProfileUpdate) {
                onProfileUpdate(profileUpdate);
              }
            }
          }
        }
      } else {
        setTokenVerified(false);
        toast({ variant: "destructive", title: "Verification Failed", description: "Please check your token and password and try again." });
      }
    } catch (error: any) {
      toast({ variant: "destructive", title: "Verification Error", description: error.message || "An error occurred during verification" });
      setTokenVerified(false);
    } finally {
      setVerificationInProgress(false);
    }
  };

  const handleDeleteMetefy = async (tokenToDelete: string) => {
    if (!user) return;
    try {
      setIsDeleting(true);
      const { error } = await supabase.from('metefy_tokens').update({
        user_id: null,
        is_verified: false,
        is_claimed: false
      }).eq('token', tokenToDelete).eq('user_id', user.id);
      
      if (error) throw error;
      
      toast({
        title: "Token removed",
        description: "Your Metefy token has been removed from your account."
      });
      
      const { data: metefyData } = await supabase
        .from('metefy_tokens')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_verified', true)
        .order('created_at', { ascending: false });
        
      if (metefyData) {
        onTokensUpdate(metefyData.map(token => ({
          token: token.token,
          password: token.password,
          dateAdded: new Date(token.created_at).toISOString().split('T')[0],
          name: token.name || null,
          call: token.call || null,
          whatsapp: token.whatsapp || null,
          x_username: token.x_username || null,
          id: token.id
        })));
      } else {
        onTokensUpdate([]);
      }
    } catch (error: any) {
      toast({
        title: "Error removing token",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Card className="bg-card border border-border shadow-sm p-6 mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-foreground">Metefy Token Verification</h2>
        </div>
        <div className="grid grid-cols-1 gap-6 mb-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="token" className="text-foreground">Metefy Token</Label>
              <Button
                type="button"
                onClick={startQrScan}
                variant="outline"
                className="flex items-center gap-2 border-border text-foreground hover:bg-muted"
                disabled={isScanning}
              >
                <QrCode className="h-4 w-4" />
                {isScanning ? 'Scanning...' : 'Scan QR'}
              </Button>
            </div>
            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2">
              {Array(12).fill(0).map((_, index) => (
                <Fragment key={index}>
                  <input
                    ref={el => inputRefs.current[index] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={tokenData.token[index] || ''}
                    onChange={(e) => handleTokenDigitChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className="w-7 h-9 sm:w-9 sm:h-12 text-center border border-border rounded-md focus:outline-none focus:border-primary focus:ring-0 bg-background text-foreground text-sm sm:text-base"
                  />
                  {(index + 1) % 4 === 0 && index < 11 && (
                    <span className="text-muted-foreground text-lg sm:text-xl mx-0.5 sm:mx-1">-</span>
                  )}
                </Fragment>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="token-password" className="text-foreground">Password</Label>
            <Input
              id="token-password"
              type="tel"
              name="password"
              value={tokenData.password}
              onChange={handleTokenChange}
              placeholder="Enter token password"
              className="w-full border-border focus:border-primary focus:ring-0 bg-background text-foreground"
              maxLength={10}
              inputMode="numeric"
              pattern="\\d*"
            />
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Button 
            onClick={handleVerifyToken} 
            disabled={!tokenData.token || tokenData.token.length !== 12 || !tokenData.password || verificationInProgress}
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            {verificationInProgress ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Verifying...
              </>
            ) : 'Verify Token'}
          </Button>
          {tokenVerified !== null && (
            <div className="flex items-center gap-2">
              {tokenVerified ? (
                <>
                  <CheckCircle className="text-green-600" size={20} />
                  <span className="text-green-600">Token verified successfully</span>
                </>
              ) : (
                <>
                  <XCircle className="text-destructive" size={20} />
                  <span className="text-destructive">Verification failed</span>
                </>
              )}
            </div>
          )}
        </div>
        
        {metefyTokens.length > 0 && (
          <div className="mt-8 space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <Key className="text-primary" />
              <span className="font-semibold text-foreground">My Tokens</span>
            </div>
            {metefyTokens.map((metefy, index) => (
              <div key={index} className="border border-border rounded-lg p-4 bg-card/50">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-foreground">Token</Label>
                    <Input value={metefy.token} disabled={true} className="bg-background text-foreground border-border" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-foreground">Password</Label>
                    <Input value={metefy.password || ''} disabled={true} className="bg-background text-foreground border-border" />
                  </div>
                </div>
                <div className="mt-2 text-sm text-foreground/80">
                  Added: {metefy.dateAdded}
                </div>
                
                <div className="flex justify-center mt-4">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" disabled={isDeleting} className="bg-destructive hover:bg-destructive/90 text-destructive-foreground">
                        {isDeleting ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> 
                            Removing...
                          </>
                        ) : (
                          <>
                            <Trash2 className="mr-2 h-4 w-4" /> 
                            Remove Token
                          </>
                        )}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-card border-border">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-foreground">Remove Metefy Token?</AlertDialogTitle>
                        <AlertDialogDescription className="text-muted-foreground">
                          This will remove the token from your account. Another user will be able to claim this token.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="border-border text-foreground hover:bg-muted">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDeleteMetefy(metefy.token)} className="bg-destructive hover:bg-destructive/90 text-destructive-foreground">
                          Remove Token
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Dialog open={showScanner} onOpenChange={(open) => {
        setShowScanner(open);
        if (!open) {
          setIsScanning(false);
          setIsTorchOn(false);
          if (qrScannerInstance) {
            qrScannerInstance.stop();
            qrScannerInstance.destroy();
            setQrScannerInstance(null);
          }
          const scannerContainer = document.getElementById('profile-qr-scanner-container');
          if (scannerContainer) {
            scannerContainer.innerHTML = '';
          }
        }
      }}>
        <DialogContent className="max-w-md bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">Scan QR Code</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Point your camera at a Metefy QR code to scan the token
            </DialogDescription>
          </DialogHeader>
          <div 
            id="profile-qr-scanner-container" 
            className="w-full h-64 bg-muted rounded-lg flex items-center justify-center"
          >
            {isScanning ? (
              <div className="text-center">
                <QrCode className="w-12 h-12 mx-auto mb-2 text-muted-foreground" />
                <p className="text-muted-foreground">Initializing camera...</p>
              </div>
            ) : (
              <div className="text-center">
                <QrCode className="w-12 h-12 mx-auto mb-2 text-muted-foreground" />
                <p className="text-muted-foreground">Camera not available</p>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button 
              variant="outline" 
              onClick={toggleTorch}
              disabled={!qrScannerInstance}
              className={`flex items-center gap-2 border-border text-foreground hover:bg-muted ${isTorchOn ? 'bg-yellow-500/10 border-yellow-500/30 text-yellow-600' : ''}`}
            >
              <Flashlight className={`h-4 w-4 ${isTorchOn ? 'text-yellow-600' : ''}`} />
              {isTorchOn ? 'Flash On' : 'Flash Off'}
            </Button>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowScanner(false);
                setIsScanning(false);
                setIsTorchOn(false);
                if (qrScannerInstance) {
                  qrScannerInstance.stop();
                  qrScannerInstance.destroy();
                  setQrScannerInstance(null);
                }
              }}
              className="border-border text-foreground hover:bg-muted"
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};


